import { type ZagruProblem } from './problem';

/**
 * Technical record of a failed request. This is what support and engineering need — and what an operator must
 * never have to read. It goes to the console (development), to telemetry (when an exporter is installed) and to a
 * small in-memory ring buffer that the Diagnostics panel reads. It is never rendered in product UI; the most a
 * screen shows is the correlation id behind "Technical details".
 */
export interface DiagnosticRecord {
  /** Correlation id sent as `X-Correlation-Id`; the key into server logs and traces. */
  id: string;
  at: string;
  method?: string;
  endpoint?: string;
  status: number;
  code?: string;
  title: string;
  detail?: string;
  /** Schema issues for `client.invalid_response`, field errors for 4xx. */
  issues?: Array<{ path: string; message: string; code?: string }>;
  tenantId?: string;
  route?: string;
}

export type DiagnosticListener = (record: DiagnosticRecord) => void;

const CAPACITY = 50;
const buffer: DiagnosticRecord[] = [];
const listeners = new Set<DiagnosticListener>();
let contextProvider: () => { tenantId?: string } = () => ({});

/** Supplies ambient context (tenant) for every record without the API layer importing the session. */
export function setDiagnosticContext(provider: () => { tenantId?: string }): void {
  contextProvider = provider;
}

function isDev(): boolean {
  try {
    return Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
  } catch {
    return false;
  }
}

export function recordDiagnostic(problem: ZagruProblem): DiagnosticRecord {
  const record: DiagnosticRecord = {
    id: problem.correlationId ?? 'n/a',
    at: new Date().toISOString(),
    method: problem.method,
    endpoint: problem.endpoint,
    status: problem.status,
    code: problem.code,
    title: problem.title,
    detail: problem.detail,
    issues: problem.diagnostics.length > 0 ? problem.diagnostics : problem.errors.map((e) => ({ path: e.field, message: e.message, code: e.code })),
    tenantId: contextProvider().tenantId,
    route: typeof window !== 'undefined' ? window.location.pathname : undefined,
  };
  // Aborted requests (navigation, unmount) are expected and not worth anyone's attention.
  if (problem.code === 'client.aborted') return record;
  buffer.push(record);
  if (buffer.length > CAPACITY) buffer.shift();
  for (const listener of listeners) {
    try {
      listener(record);
    } catch {
      /* a broken listener must not break the request path */
    }
  }
  if (isDev() && typeof console !== 'undefined') {
    const label = problem.code === 'client.invalid_response' ? 'contract mismatch' : 'request failed';
    console.warn(`[zagru] ${label}: ${record.method ?? ''} ${record.endpoint ?? ''} -> ${record.status} ${record.code ?? ''} (${record.id})`, record);
  }
  return record;
}

/** Most recent first. */
export function recentDiagnostics(): DiagnosticRecord[] {
  return [...buffer].reverse();
}

export function onDiagnostic(listener: DiagnosticListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Clears the buffer (tests, "Clear" in the Diagnostics panel). */
export function clearDiagnostics(): void {
  buffer.length = 0;
}
